/**
 * Wer kommt, wer geht - der Hinweis im Warteraum
 * (Produktentscheidung vom 15.10.2026).
 *
 * Robin: "Es muss ein Hinweis geben, dass jemand 1. der Lobby gejoined ist
 * und 2. wenn wieder jemand geleaved ist."
 *
 * Der Client bekommt vom Server keine Ereignisliste, sondern immer nur den
 * aktuellen Stand. "Jemand ist gekommen" ist fuer ihn deshalb nichts anderes
 * als: Im letzten Schnappschuss war der Platz leer, in diesem sitzt jemand
 * darauf. Genau dieser Vergleich steht hier - als eigene Regel, nicht
 * versteckt in der Anzeige, damit er sich ohne Bild pruefen laesst.
 *
 * Verglichen wird die SITZUNG des Gegners, nicht bloss "ist da jemand". Sonst
 * bliebe ein Wechsel unbemerkt, bei dem einer geht und im selben Augenblick
 * der naechste hereinkommt.
 */

/** Was im Warteraum passiert ist. */
export type LobbyEreignis = 'gegnerDa' | 'gegnerWeg';

/** Der Stand des letzten Vergleichs - mehr braucht die Regel nicht. */
export interface LobbyGedaechtnis {
  /** Sitzung des Gegners. Leer heisst: niemand da. */
  gegner: string;
}

/** Der neue Stand aus dem Schnappschuss. */
export interface LobbyStand extends LobbyGedaechtnis {
  /**
   * Ist der Gegner der Testgegner?
   *
   * Der erscheint nicht und geht nicht - er gehoert zum Match und sitzt von
   * der ersten Sekunde an da. Ein "An opponent joined" waere dort eine
   * Meldung ueber nichts.
   */
  gegnerIstBot: boolean;
  /**
   * Sitzt man ueberhaupt im Warteraum?
   *
   * Im Match und im Ergebnisbildschirm wird nichts gemeldet; dort hat ein
   * Abgang andere Folgen, und die stehen schon im Bild (dieselbe
   * Ueberlegung wie bei matchToene).
   */
  imWarteraum: boolean;
}

export interface LobbyErgebnis {
  /** Was zu melden ist. null heisst: nichts Neues. */
  ereignis: LobbyEreignis | null;
  /** Der Stand fuer den naechsten Vergleich. */
  gedaechtnis: LobbyGedaechtnis;
}

/**
 * Vergleicht den letzten Stand mit dem neuen.
 *
 * Der Stand wird AUCH DANN fortgeschrieben, wenn nichts gemeldet wird - sonst
 * traefe den naechsten Vergleich derselbe Sprung noch einmal, und nach einem
 * Match stuende sofort "An opponent joined" da (genau der Fehler, der am
 * 15.10.2026 beim Ton aufgefallen ist).
 */
export function lobbyEreignis(vorher: LobbyGedaechtnis, jetzt: LobbyStand): LobbyErgebnis {
  const gedaechtnis: LobbyGedaechtnis = { gegner: jetzt.gegner };

  if (!jetzt.imWarteraum) return { ereignis: null, gedaechtnis };
  if (vorher.gegner === jetzt.gegner) return { ereignis: null, gedaechtnis };

  // Niemand mehr da: Der Platz ist frei geworden.
  if (jetzt.gegner === '') return { ereignis: 'gegnerWeg', gedaechtnis };

  // Jemand sitzt da, der vorher nicht da war - auch bei einem Wechsel.
  if (jetzt.gegnerIstBot) return { ereignis: null, gedaechtnis };
  return { ereignis: 'gegnerDa', gedaechtnis };
}
